import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { IconCheck, IconChevronDown } from '@tabler/icons-react';

/**
 * Compact dropdown used across the QR sidebar.
 *
 * Options may carry a `thumb` node (the shape previews), which is shown both in
 * the closed trigger and in the list, so the picker stays visual while taking
 * one row instead of a grid.
 *
 * @param {{value:any,label:string,thumb?:React.ReactNode,hint?:string}[]} options
 */
export default function QrSelect({ value, options, onChange, placeholder, ariaLabel }) {
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const wrapRef = useRef(null);
    const listRef = useRef(null);
    const listId = useId();

    const selectedIndex = options.findIndex((opt) => opt.value === value);
    const selected = selectedIndex >= 0 ? options[selectedIndex] : null;

    const close = useCallback(() => {
        setOpen(false);
        setActiveIndex(-1);
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (e) => {
            if (!wrapRef.current?.contains(e.target)) close();
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [open, close]);

    useEffect(() => {
        if (!open) return;
        setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    }, [open, selectedIndex]);

    useEffect(() => {
        if (!open || activeIndex < 0) return;
        listRef.current?.children?.[activeIndex]?.scrollIntoView({ block: 'nearest' });
    }, [open, activeIndex]);

    const commit = (index) => {
        const opt = options[index];
        if (!opt) return;
        onChange(opt.value);
        close();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            close();
            return;
        }
        if (!open) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                e.preventDefault();
                setOpen(true);
            }
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((i) => (i + 1) % options.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => (i - 1 + options.length) % options.length);
        } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            commit(activeIndex);
        } else if (e.key === 'Home') {
            e.preventDefault();
            setActiveIndex(0);
        } else if (e.key === 'End') {
            e.preventDefault();
            setActiveIndex(options.length - 1);
        }
    };

    return (
        <div ref={wrapRef} className="relative">
            <button
                type="button"
                onClick={() => (open ? close() : setOpen(true))}
                onKeyDown={handleKeyDown}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={open ? listId : undefined}
                aria-label={ariaLabel}
                className={`flex w-full items-center gap-2 rounded-lg border bg-white px-2.5 py-2 text-left transition-colors dark:bg-zinc-900 ${
                    open
                        ? 'border-amber-500 dark:border-amber-500'
                        : 'border-[#ECE8E4] hover:border-amber-400/60 dark:border-zinc-700'
                }`}
            >
                {selected?.thumb ? (
                    <span className="flex h-6 w-6 flex-none items-center justify-center text-[#3D3833] dark:text-zinc-200 [&_svg]:h-6 [&_svg]:w-auto">
                        {selected.thumb}
                    </span>
                ) : null}
                <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                    {selected ? selected.label : (placeholder || '')}
                </span>
                {selected?.hint ? (
                    <span className="flex-none text-[11px] font-medium text-[#6B635B] dark:text-zinc-400">{selected.hint}</span>
                ) : null}
                <IconChevronDown size={16} className={`flex-none text-[#A8A099] transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open ? (
                <ul
                    ref={listRef}
                    id={listId}
                    role="listbox"
                    tabIndex={-1}
                    className="absolute left-0 right-0 z-30 mt-1 max-h-[248px] overflow-y-auto rounded-xl border border-[#ECE8E4] bg-white p-1 shadow-[0_12px_28px_rgba(28,24,21,0.14)] dark:border-zinc-700 dark:bg-zinc-900"
                >
                    {options.map((opt, index) => {
                        const isSelected = opt.value === value;
                        return (
                            <li key={String(opt.value)}>
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={isSelected}
                                    onMouseEnter={() => setActiveIndex(index)}
                                    onClick={() => commit(index)}
                                    className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors ${
                                        index === activeIndex
                                            ? 'bg-amber-50 dark:bg-amber-900/25'
                                            : 'bg-transparent'
                                    }`}
                                >
                                    {opt.thumb ? (
                                        <span className={`flex h-7 w-7 flex-none items-center justify-center [&_svg]:h-7 [&_svg]:w-auto ${
                                            isSelected ? 'text-amber-700 dark:text-amber-300' : 'text-[#3D3833] dark:text-zinc-200'
                                        }`}
                                        >
                                            {opt.thumb}
                                        </span>
                                    ) : null}
                                    <span className={`min-w-0 flex-1 truncate text-[12.5px] font-semibold ${
                                        isSelected ? 'text-amber-700 dark:text-amber-300' : 'text-[#3D3833] dark:text-zinc-200'
                                    }`}
                                    >
                                        {opt.label}
                                    </span>
                                    {opt.hint ? (
                                        <span className="flex-none text-[11px] font-medium text-[#6B635B] dark:text-zinc-400">{opt.hint}</span>
                                    ) : null}
                                    {isSelected ? <IconCheck size={15} className="flex-none text-amber-600" /> : null}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            ) : null}
        </div>
    );
}
